import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { EnterpriseLayout } from "@/components/layout/EnterpriseLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import axios from "axios";
import {
  Settings, Building2, CreditCard, Bell, Shield,
  Save, Key, DollarSign, Percent
} from "lucide-react";
import { toast } from "sonner";

export const EnterpriseSettings = () => {
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [settings, setSettings] = useState({
    approval_required: true,
    approval_threshold: 50000,
    auto_approve_below: 10000,
    punchout_enabled: true,
    notifications_enabled: true
  });
  const [globalSettings, setGlobalSettings] = useState({
    usd_to_htg_rate: 148,
    bvglinkamazon_fee_percent: 0.10,
    amazon_tax_percent: 0.10,
    meru_fee_percent: 0.12,
    crypto_fee_percent: 0.05,
    marketplace_commission_percent: 0.10
  });
  const [saving, setSaving] = useState(false);
  const [fetchingGlobal, setFetchingGlobal] = useState(false);

  const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

  const token = localStorage.getItem('enterprise_token');
  const user = JSON.parse(localStorage.getItem('enterprise_user') || '{}');

  useEffect(() => {
    if (!token) {
      navigate('/enterprise');
      return;
    }

    const storedCompany = localStorage.getItem('enterprise_company');
    if (storedCompany) {
      const companyData = JSON.parse(storedCompany);
      setCompany(companyData);
      if (companyData.settings) {
        setSettings(prev => ({ ...prev, ...companyData.settings }));
      }
    }

    // Check if user has permission
    if (!['admin', 'super_admin'].includes(user.role)) {
      toast.error("Ou pa gen aksè pou paramèt yo");
      navigate('/enterprise/dashboard');
    }

    if (user.role === 'super_admin') {
      fetchGlobalSettings();
    }
  }, [token, navigate, user.role]);

  const fetchGlobalSettings = async () => {
    setFetchingGlobal(true);
    try {
      const response = await axios.get(`${API}/admin/settings`);
      setGlobalSettings(response.data);
    } catch (error) {
      console.error("Error fetching global settings:", error);
    } finally {
      setFetchingGlobal(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // 1. Save Enterprise Settings (Placeholder in this repo, usually an API call)
      // await axios.post(`${API}/enterprise/settings`, settings, { headers: { Authorization: `Bearer ${token}` } });

      // 2. Save Global Settings if Super Admin
      if (user.role === 'super_admin') {
        await axios.post(`${API}/admin/settings`, globalSettings);
        toast.success("Paramèt sistèm yo anrejistre tou!");
      }

      toast.success("Paramèt yo sove!");
    } catch (error) {
      toast.error("Erè nan anrejistre paramèt yo");
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-HT').format(amount);
  };

  return (
    <EnterpriseLayout>
      <div className="space-y-6" data-testid="enterprise-settings">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-[#232F3E]">Paramèt</h1>
          <p className="text-gray-500">Konfigire paramèt antrepriz la</p>
        </div>

        {/* Company Info */}
        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-[#FF9900]" />
              Enfòmasyon Antrepriz
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Non Antrepriz</Label>
                <Input value={company?.name || ''} disabled className="bg-gray-50" />
              </div>
              <div className="space-y-2">
                <Label>Kòd</Label>
                <Input value={company?.code || ''} disabled className="bg-gray-50" />
              </div>
              <div className="space-y-2">
                <Label>Imèl</Label>
                <Input value={company?.email || ''} disabled className="bg-gray-50" />
              </div>
              <div className="space-y-2">
                <Label>Telefòn</Label>
                <Input value={company?.phone || ''} disabled className="bg-gray-50" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Approval Settings */}
        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-[#FF9900]" />
              Paramèt Apwobasyon
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Egzije Apwobasyon</p>
                <p className="text-sm text-gray-500">Kòmand yo bezwen apwobasyon anvan peman</p>
              </div>
              <Switch
                checked={settings.approval_required}
                onCheckedChange={(checked) => setSettings({ ...settings, approval_required: checked })}
                data-testid="approval-required-switch"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Sèy Apwobasyon (HTG)</Label>
                <Input
                  type="number"
                  value={settings.approval_threshold}
                  onChange={(e) => setSettings({ ...settings, approval_threshold: parseInt(e.target.value) })}
                  data-testid="approval-threshold"
                />
                <p className="text-xs text-gray-500">
                  Kòmand pi wo pase sa bezwen apwobasyon espesyal
                </p>
              </div>
              <div className="space-y-2">
                <Label>Oto-Apwouve Anba (HTG)</Label>
                <Input
                  type="number"
                  value={settings.auto_approve_below}
                  onChange={(e) => setSettings({ ...settings, auto_approve_below: parseInt(e.target.value) })}
                  data-testid="auto-approve-below"
                />
                <p className="text-xs text-gray-500">
                  Kòmand pi ba pase sa apwouve otomatikman
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Punchout Settings */}
        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-[#FF9900]" />
              Paramèt Punchout
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Punchout Aktive</p>
                <p className="text-sm text-gray-500">Pèmèt itilizatè yo achte sou Amazon Business</p>
              </div>
              <Switch
                checked={settings.punchout_enabled}
                onCheckedChange={(checked) => setSettings({ ...settings, punchout_enabled: checked })}
                data-testid="punchout-enabled-switch"
              />
            </div>

            <div className="p-4 bg-gray-50 rounded-xl">
              <div className="flex items-center gap-2 mb-2">
                <Key className="w-5 h-5 text-[#FF9900]" />
                <span className="font-medium">Kredansyèl Amazon Business</span>
              </div>
              <p className="text-sm text-gray-600">
                Koneksyon Amazon Business konfigire. Kontakte sipò pou modifye kredansyèl yo.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Notifications */}
        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-[#FF9900]" />
              Notifikasyon
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Notifikasyon Imèl</p>
                <p className="text-sm text-gray-500">Resevwa notifikasyon lè gen nouvo kòmand oswa apwobasyon</p>
              </div>
              <Switch
                checked={settings.notifications_enabled}
                onCheckedChange={(checked) => setSettings({ ...settings, notifications_enabled: checked })}
                data-testid="notifications-switch"
              />
            </div>
          </CardContent>
        </Card>

        {/* Global Settings (Only for Super Admin) */}
        {user.role === 'super_admin' && (
          <Card className="border-0 shadow-lg border-l-4 border-l-[#FF9900]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="w-5 h-5 text-[#FF9900]" />
                Paramèt Sistèm (Global - Taux & Frè)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <Label className="text-sm font-semibold">Taux Echanj (1 USD = X HTG)</Label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      type="number"
                      step="0.1"
                      value={globalSettings.usd_to_htg_rate}
                      onChange={(e) => setGlobalSettings({ ...globalSettings, usd_to_htg_rate: parseFloat(e.target.value) })}
                      className="pl-10 text-lg font-bold border-[#FF9900] bg-orange-50/50"
                    />
                  </div>
                  <p className="text-[10px] text-gray-400 italic">Chanje prix HTG a pou tout sit la.</p>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-semibold">Frè Sèvis (%)</Label>
                  <div className="relative">
                    <Percent className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      type="number"
                      step="1"
                      value={globalSettings.bvglinkamazon_fee_percent * 100}
                      onChange={(e) => setGlobalSettings({ ...globalSettings, bvglinkamazon_fee_percent: parseFloat(e.target.value) / 100 })}
                      className="pl-10"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-semibold">Taks Amazon (%)</Label>
                  <div className="relative">
                    <Percent className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      type="number"
                      step="1"
                      value={globalSettings.amazon_tax_percent * 100}
                      onChange={(e) => setGlobalSettings({ ...globalSettings, amazon_tax_percent: parseFloat(e.target.value) / 100 })}
                      className="pl-10"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-semibold">Komisyon Market (%)</Label>
                  <div className="relative">
                    <Percent className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      type="number"
                      step="1"
                      value={(globalSettings.marketplace_commission_percent || 0) * 100}
                      onChange={(e) => setGlobalSettings({ ...globalSettings, marketplace_commission_percent: parseFloat(e.target.value) / 100 })}
                      className="pl-10"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-semibold">Frè Meru (%)</Label>
                  <div className="relative">
                    <Percent className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      type="number"
                      step="1"
                      value={(globalSettings.meru_fee_percent || 0) * 100}
                      onChange={(e) => setGlobalSettings({ ...globalSettings, meru_fee_percent: parseFloat(e.target.value) / 100 })}
                      className="pl-10"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-semibold">Frè USDT (%)</Label>
                  <div className="relative">
                    <Percent className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      type="number"
                      step="1"
                      value={(globalSettings.crypto_fee_percent || 0) * 100}
                      onChange={(e) => setGlobalSettings({ ...globalSettings, crypto_fee_percent: parseFloat(e.target.value) / 100 })}
                      className="pl-10"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Save Button */}
        <div className="flex justify-end">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-[#FF9900] hover:bg-[#F08C00] text-black font-bold px-8"
            data-testid="save-settings-btn"
          >
            {saving ? (
              "Ap sove..."
            ) : (
              <>
                <Save className="w-5 h-5 mr-2" />
                Sove Paramèt
              </>
            )}
          </Button>
        </div>
      </div>
    </EnterpriseLayout>
  );
};

